'use client';

import { memo } from 'react';

/**
 * SectionProgressRing — the in-section progress indicator, as a compact ring.
 *
 * The circular sibling of {@link SectionProgressLine}: same "how far through THIS
 * section am I" meaning and the same 0..1 `fraction` contract, but rendered as a
 * small ring that lives top-right under the overall journey stepper instead of a
 * full-width hairline. Members found the line easy to confuse with the overall
 * bar; a small ring in the corner reads clearly as a distinct, secondary signal
 * and frees the bottom of the screen for the primary action.
 *
 * Determinate: the accent arc fills clockwise from 12 o'clock, proportional to
 * `fraction`, animating via a `stroke-dashoffset` transition (the ring analogue
 * of the line's width transition). When `indeterminate` (a sensagram whose
 * length isn't known yet), a short arc spins slowly instead of showing a fake
 * position. Both collapse to a static state under reduced motion.
 *
 * Pure CSS/SVG by design — no framer-motion — so it stays cheap to mount on a
 * busy surface (incl. inside the FormExecutor) and never tips the timing-fragile
 * FormExecutor parity suite. The keyframes are inlined so the component is
 * self-contained wherever it renders (member frontend and hub previews alike).
 */

// Keyframe for the indeterminate spinner only. Deliberately NOT injected on the
// determinate path: the determinate arc animates via Tailwind utility classes
// (prebuilt stylesheet, zero runtime cost), so the common case — including every
// in-form ring — adds no runtime <style>. That parity with the old line matters:
// a per-render <style> injection added enough style-recalc under full-suite CPU
// contention to tip the timing-fragile FormExecutor auto-submit race
// ([formexecutor-parity-submit-flake]). The spinner keyframe can't be expressed
// as a utility, so it ships as a <style> only when actually indeterminate (the
// sensagram unknown-length case — never on a form screen).
const SPINNER_STYLE =
  '@keyframes sv-ring-spin{to{transform:rotate(360deg)}}' +
  '.sv-ring-spinner{transform-box:fill-box;transform-origin:center;animation:sv-ring-spin 1.4s linear infinite}' +
  '@media (prefers-reduced-motion:reduce){.sv-ring-spinner{animation:none}}';

export interface SectionProgressRingProps {
  /** 0..1 completion fraction. Ignored when `indeterminate`. */
  fraction?: number;
  /** When true, render a slow spinner for an unknown-length section. */
  indeterminate?: boolean;
  /** Accessible label for the progressbar (e.g. the section title). */
  ariaLabel: string;
  /** Human-readable value text (e.g. "3 of 8 answered"). */
  ariaValueText?: string;
  ariaValueNow?: number;
  ariaValueMin?: number;
  ariaValueMax?: number;
  /** Diameter in px. Default 34 — small and calm. */
  size?: number;
}

function SectionProgressRingImpl({
  fraction = 0,
  indeterminate = false,
  ariaLabel,
  ariaValueText,
  ariaValueNow,
  ariaValueMin = 0,
  ariaValueMax = 100,
  size = 34,
}: SectionProgressRingProps) {
  const stroke = 3;
  const center = size / 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, fraction));
  const pct = clamped * 100;
  const dashOffset = circumference * (1 - clamped);

  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuetext={indeterminate ? undefined : ariaValueText}
      aria-valuemin={indeterminate ? undefined : ariaValueMin}
      aria-valuemax={indeterminate ? undefined : ariaValueMax}
      aria-valuenow={indeterminate ? undefined : (ariaValueNow ?? Math.round(pct))}
      className="inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        aria-hidden="true"
      >
        {/* Track — a faint full ring, the same neutral as the journey rail's
            upcoming segments so the two progress objects feel related. */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="rgba(26,35,50,0.12)"
          strokeWidth={stroke}
        />
        {indeterminate ? (
          <>
            <style>{SPINNER_STYLE}</style>
            <circle
              data-ring-spinner
              className="sv-ring-spinner"
              cx={center}
              cy={center}
              r={radius}
              stroke="var(--color-accent)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${circumference * 0.25} ${circumference}`}
            />
          </>
        ) : (
          <circle
            data-ring-arc
            className="[transition:stroke-dashoffset_400ms_ease-out] motion-reduce:[transition:none]"
            cx={center}
            cy={center}
            r={radius}
            stroke="var(--color-accent)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            style={{ strokeDashoffset: dashOffset }}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </svg>
    </div>
  );
}

/**
 * Memoized so a parent re-render that doesn't change the ring's props (e.g. a
 * sequential form's final-option tap, where the fraction is already at its max)
 * skips re-rendering the SVG entirely. Beyond the obvious win, this keeps the
 * ring out of the FormExecutor's timing-fragile auto-submit window
 * ([formexecutor-parity-submit-flake]): no ring work competes with the submit's
 * async payload-hash digest under CPU contention. Props are primitives, so the
 * default shallow comparison is exactly right.
 */
export const SectionProgressRing = memo(SectionProgressRingImpl);
